import { useMemo } from 'react';
import {
  useCompanyInvitationsQuery,
  useCompanyUsersQuery,
  useGetUserRoleInCompanyQuery,
} from 'models';
import { useCurrentCompanyId } from 'utils/hooks';
import { TeamMember } from 'containers/TeamMembers';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';

export enum TeamMembersFilterOption {
  Active = 'active',
  Pending = 'pending',
  Deactivated = 'deactivated',
  All = 'all',
}

export enum UserStatus {
  Active = 'active',
  Deactivated = 'deactivated',
  Pending = 'pending',
}

export const useTeamMembers = (filterBy: TeamMembersFilterOption) => {
  const { companyId } = useCurrentCompanyId();
  const { data, loading: loadingInvitations } = useCompanyInvitationsQuery({
    variables: {
      id: companyId,
    },
  });
  const { data: usersData, loading: loadingUsers } = useCompanyUsersQuery({
    variables: {
      id: companyId,
    },
  });

  const members = useMemo(() => {
    const invitations = data?.invitations || [];
    const invited = (invitations || [])
      .filter(({ status }) => status !== UserStatus.Active)
      .map(({ userEmail, status, role, id, user }) => ({
        id,
        email: userEmail ?? '',
        role,
        disabled: status !== UserStatus.Pending,
        pending: status === UserStatus.Pending,
        user,
        type: 'invitation',
      }));

    const active = (usersData?.users || []).map((user) => ({
      id: user.id,
      name: user.displayName,
      email: user.email,
      role: user.companyUsers?.[0]?.role,
      disabled: user.disabled,
      user: {
        id: user.id,
        email: user.email,
        avatarUrl: user.avatarUrl,
        displayName: user.displayName,
      },
      type: 'user',
    }));

    if (filterBy === TeamMembersFilterOption.All) {
      return [...active, ...invited];
    }
    if (filterBy === TeamMembersFilterOption.Active) {
      return active.filter((u) => !u.disabled);
    }
    if (filterBy === TeamMembersFilterOption.Pending) {
      return active.filter((u) => u.disabled);
    }
    return invited;
  }, [filterBy, usersData, data]) as TeamMember[];

  return {
    members,
    loading: loadingInvitations || loadingUsers,
  };
};

export const useUserRole = () => {
  const { user } = useAuth();
  const { companyId } = useCurrentCompanyId();

  const { data, loading, error } = useGetUserRoleInCompanyQuery({
    variables: {
      companyId,
      userId: user?.id,
    },
    skip: !user || !companyId,
  });

  return {
    role: data?.roleInCompany?.role,
    loading,
    error,
  };
};
